/**
 * The handle a building entity's geometry resolves to when picked.
 * @module
 */
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { BuildingEntityHandle } from "./buildingEntityHandle";
import type { BuildingEntityType } from "./buildingEntityTypeTypes";

/**
 * A stored `pickHandle` wins: generation sets it when a click must select something other than the
 * entity itself, e.g. a door inside a component instance selects the instance. Otherwise entities
 * backed 1:1 by a doc entity are selected as that doc entity, and the rest exist only on the
 * generated building and are addressed by their entity type and id.
 *
 * This mirrors how the generation-time allocator chose which handle to spend an integer on, so an
 * entity's pick handle names the same thing its `integerId` did.
 */
export function buildingEntityPickHandle(entity: {
  readonly entityType: BuildingEntityType;
  readonly id: string;
  readonly pickHandle?: DocEntityHandle;
  readonly docEntityHandle?: DocEntityHandle;
}): DocEntityHandle {
  return entity.pickHandle ?? entity.docEntityHandle ?? generatedBuildingEntityHandle(entity.entityType, entity.id);
}

/** The handle for a building entity that has no doc entity behind it. */
export function generatedBuildingEntityHandle(entityType: BuildingEntityType, id: string): DocEntityHandle {
  return { type: "GENERATED_ENTITY", entity: { entityType, id } as BuildingEntityHandle };
}
